import { US_TIME_ZONES } from '../../../core/shifts/shiftTimeZone';
import React, {useEffect, useState} from "react";

const DEFAULT_ROLES = [
  "CNA",
  "LVN",
  "RN",
  "MED_AIDE",
  "CARE_GIVER",
];

function inputStyle() {
  return {
    width: "100%",
    minHeight: 40,
    padding: "9px 11px",
    borderRadius: 10,
    border: "1px solid #d8e1ea",
    background: "#ffffff",
    color: "#0f172a",
    fontSize: 13,
    fontWeight: 700,
    outline: "none",
    boxSizing: "border-box",
  };
}

function fieldLabelStyle() {
  return {
    marginBottom: 6,
    color: "#475569",
    fontSize: 12,
    fontWeight: 900,
  };
}

function sectionStyle() {
  return {
    marginTop: 18,
    padding: 14,
    borderRadius: 18,
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
  };
}

function sectionTitleStyle() {
  return {
    marginBottom: 10,
    color: "#0f172a",
    fontWeight: 900,
  };
}

function visibilityLabel(rule) {
  const labels = {
    facilityCanSeePayroll: "Facility Can See Payroll",
    facilityCanSeeMargin: "Facility Can See Margin",
    facilityCanSeeWorkersOutsideFacility:
      "Facility Can See Workers Outside Facility",
  };

  return labels[rule] || rule;
}

export default function Facility360Profile({
  facility,
  onChangeFacility,
  layoutMid = true,
  onSaveTimeZone,
}) {
  const [zone, setZone] = useState(facility?.timeZone || facility?.timezone || '');
  const [zoneMessage, setZoneMessage] = useState('');
  useEffect(() => { setZone(facility?.timeZone || facility?.timezone || ''); setZoneMessage(''); }, [facility?.id, facility?.timeZone, facility?.timezone]);
  const saveZone = async () => {
    try { await onSaveTimeZone(zone); setZoneMessage('Timezone saved.'); }
    catch (error) { setZoneMessage(error.message || 'Timezone could not be saved.'); }
  };
  const updateFacility = (updater) => {
    if (typeof onChangeFacility !== "function") {
      return;
    }

    onChangeFacility((previous) => {
      const current = previous || facility || {};

      return typeof updater === "function"
        ? updater(current)
        : {
            ...current,
            ...updater,
          };
    });
  };

  const roles =
    Array.isArray(facility?.allowedRoles) &&
    facility.allowedRoles.length > 0
      ? facility.allowedRoles
      : DEFAULT_ROLES;

  return (
    <>
      <label style={{display: 'block', marginBottom: 16}}>Timezone
        <select style={inputStyle()} value={zone}
          onChange={event => setZone(event.target.value)}>
          <option value="" disabled>Select facility timezone (otherwise tenant default applies)</option>
          {US_TIME_ZONES.map(([zone, label]) => <option key={zone} value={zone}>{label} ({zone})</option>)}
        </select>
        {onSaveTimeZone && <button type="button" onClick={saveZone}>Save timezone</button>}
        {zoneMessage && <span role="status">{zoneMessage}</span>}
      </label>
      {/* Core Facility Profile */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: layoutMid
            ? "1fr 1fr"
            : "1fr",
          gap: 14,
        }}
      >
        {[
          ["name", "Facility Name"],
          ["tenantId", "Tenant ID"],
          ["tenantName", "Tenant Name"],
          ["tenantType", "Tenant Type"],
        ].map(([key, label]) => {
          const locked = [
            "tenantId",
            "tenantName",
            "tenantType",
          ].includes(key);

          return (
            <div key={key}>
              <div style={fieldLabelStyle()}>
                {label}
              </div>

              <input
                value={facility?.[key] || ""}
                disabled={locked}
                onChange={(e) =>
                  updateFacility((previous) => ({
                    ...previous,
                    [key]: e.target.value,
                  }))
                }
                style={{
                  ...inputStyle(),
                  background: locked
                    ? "#f1f5f9"
                    : "#ffffff",
                }}
              />
            </div>
          );
        })}

        <div>
          <div style={fieldLabelStyle()}>
            Billing Model
          </div>

          <select
            value={
              facility?.billingModel ||
              "agency_billing"
            }
            onChange={(e) =>
              updateFacility((previous) => ({
                ...previous,
                billingModel: e.target.value,
              }))
            }
            style={inputStyle()}
          >
            <option value="agency_billing">
              Agency Billing
            </option>

            <option value="contract_billrate">
              Contract Bill Rate
            </option>

            <option value="internal_cost_center">
              Internal Cost Center
            </option>

            <option value="blended">
              Blended
            </option>
          </select>
        </div>

        <div>
          <div style={fieldLabelStyle()}>
            Facility Status
          </div>

          <select
            value={facility?.status || "active"}
            onChange={(e) =>
              updateFacility((previous) => ({
                ...previous,
                status: e.target.value,
              }))
            }
            style={inputStyle()}
          >
            <option value="active">
              Active
            </option>

            <option value="inactive">
              Inactive
            </option>
          </select>
        </div>
      </div>

      {/* Facility Location */}
      <div style={sectionStyle()}>
        <div style={sectionTitleStyle()}>
          Facility Location
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: layoutMid
              ? "1fr 1fr"
              : "1fr",
            gap: 12,
          }}
        >
          <input
            value={facility?.address || ""}
            placeholder="Street Address"
            style={inputStyle()}
            onChange={(e) =>
              updateFacility((previous) => ({
                ...previous,
                address: e.target.value,
              }))
            }
          />

          <input
            value={facility?.city || ""}
            placeholder="City"
            style={inputStyle()}
            onChange={(e) =>
              updateFacility((previous) => ({
                ...previous,
                city: e.target.value,
              }))
            }
          />

          <input
            value={facility?.state || ""}
            placeholder="State"
            style={inputStyle()}
            onChange={(e) =>
              updateFacility((previous) => ({
                ...previous,
                state: e.target.value,
              }))
            }
          />

          <input
            value={
              facility?.zip ||
              facility?.zipCode ||
              ""
            }
            placeholder="ZIP"
            style={inputStyle()}
            onChange={(e) =>
              updateFacility((previous) => ({
                ...previous,
                zip: e.target.value,
                zipCode: e.target.value,
              }))
            }
          />

          <input
            type="number"
            step="any"
            value={facility?.facilityLat ?? ""}
            placeholder="Latitude"
            style={inputStyle()}
            onChange={(e) =>
              updateFacility((previous) => ({
                ...previous,
                facilityLat:
                  e.target.value === ""
                    ? ""
                    : Number(e.target.value),
              }))
            }
          />

          <input
            type="number"
            step="any"
            value={facility?.facilityLng ?? ""}
            placeholder="Longitude"
            style={inputStyle()}
            onChange={(e) =>
              updateFacility((previous) => ({
                ...previous,
                facilityLng:
                  e.target.value === ""
                    ? ""
                    : Number(e.target.value),
              }))
            }
          />

          <input
            type="number"
            min="1"
            step="1"
            value={
              facility?.geoRadiusMeters ?? ""
            }
            placeholder="Arrival Radius (meters)"
            style={inputStyle()}
            onChange={(e) =>
              updateFacility((previous) => ({
                ...previous,
                geoRadiusMeters:
                  e.target.value === ""
                    ? ""
                    : Number(e.target.value),
              }))
            }
          />
        </div>
      </div>

      {/* Rates Matrix */}
      <div style={sectionStyle()}>
        <div style={sectionTitleStyle()}>
          Rates Matrix
        </div>

        <p style={{ color: "#64748b", fontSize: 13 }}>Hourly billing and worker pay rates by role.</p>
        {layoutMid && <div style={{ display: "grid", gridTemplateColumns: "120px 1fr 1fr", gap: 10, padding: "0 10px 8px", fontSize: 12, fontWeight: 700 }}>
          <span>Role</span><span>Bill rate / hour</span><span>Worker pay / hour</span>
        </div>}
        {roles.map((role) => (
          <div
            key={role}
            style={{
              display: "grid",
              gridTemplateColumns: layoutMid
                ? "120px 1fr 1fr"
                : "1fr",
              gap: 10,
              alignItems: "center",
              marginBottom: 10,
              padding: 10,
              borderRadius: 12,
              background: "#ffffff",
              border: "1px solid #e2e8f0",
            }}
          >
            <div
              style={{
                color: "#0f172a",
                fontSize: 13,
                fontWeight: 900,
              }}
            >
              {role}
            </div>

            <input
              type="number"
              aria-label={`${role} bill rate per hour`}
              placeholder="Bill Rate"
              value={
                facility?.rates?.[role]?.billRate ??
                ""
              }
              onChange={(e) =>
                updateFacility((previous) => ({
                  ...previous,
                  rates: {
                    ...(previous?.rates || {}),
                    [role]: {
                      ...(previous?.rates?.[role] ||
                        {}),
                      billRate:
                        e.target.value === ""
                          ? ""
                          : Number(e.target.value),
                    },
                  },
                }))
              }
              style={inputStyle()}
            />

            <input
              type="number"
              aria-label={`${role} worker pay rate per hour`}
              placeholder="Pay Rate"
              value={
                facility?.rates?.[role]?.payRate ??
                ""
              }
              onChange={(e) =>
                updateFacility((previous) => ({
                  ...previous,
                  rates: {
                    ...(previous?.rates || {}),
                    [role]: {
                      ...(previous?.rates?.[role] ||
                        {}),
                      payRate:
                        e.target.value === ""
                          ? ""
                          : Number(e.target.value),
                    },
                  },
                }))
              }
              style={inputStyle()}
            />
          </div>
        ))}
      </div>

      {/* Visibility Rules */}
      <div style={sectionStyle()}>
        <div style={sectionTitleStyle()}>
          Visibility Rules
        </div>

        <div
          style={{
            display: "grid",
            gap: 8,
          }}
        >
          {[
            "facilityCanSeePayroll",
            "facilityCanSeeMargin",
            "facilityCanSeeWorkersOutsideFacility",
          ].map((rule) => (
            <div
              key={rule}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 12,
                padding: "10px 12px",
                borderRadius: 12,
                background: "#ffffff",
                border: "1px solid #e2e8f0",
              }}
            >
              <div
                style={{
                  color: "#334155",
                  fontSize: 13,
                  fontWeight: 800,
                }}
              >
                {visibilityLabel(rule)}
              </div>

              <input
                type="checkbox"
                checked={
                  facility?.visibility?.[rule] ===
                  true
                }
                onChange={(e) =>
                  updateFacility((previous) => ({
                    ...previous,
                    visibility: {
                      ...(previous?.visibility ||
                        {}),
                      [rule]: e.target.checked,
                    },
                  }))
                }
              />
            </div>
          ))}
        </div>
      </div>
    </>
  );
}